import React, { useEffect, useState, Fragment } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
} from "reactstrap";
import { toast } from "react-toastify";
import { updateUser, getUserById, changeUserPassword } from "../../api/user.api";
import { useTranslation } from "react-i18next";
import { titleCase } from "../../utils/case";
import { Breadcrumbs } from "../../AbstractElements";

const Profile = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [form, setForm] = useState({
    id: null,
    name: "",
    username: "",
    email: "",
    role: "",
    status: "active",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // ================= FETCH USER =================
  const fetchUser = async (id) => {
    try {
      const data = await getUserById(id);

      setForm((prev) => ({
        ...prev,
        id: data.id,
        name: data.name || "",
        username: data.username || "",
        email: data.email || "",
        role: data.role || "",
        status: data.status || "active",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ================= INIT =================
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));

    if (storedUser?.id) {
      fetchUser(storedUser.id);
    } else {
      toast.error("user not found");
    }
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ================= UPDATE PROFILE =================
  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!form.id) {
        return toast.error("user not found");
      }

      setLoading(true);

      await updateUser(form.id, {
        name: form.name,
        username: form.username,
        email: form.email,
      });

      toast.success("profile updated successfully");

      await fetchUser(form.id);

      const updatedUser = {
        ...JSON.parse(localStorage.getItem("user")),
        name: form.name,
        username: form.username,
        email: form.email,
        role: form.role,
        status: form.status,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ================= CHANGE PASSWORD =================
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!form.id) {
        return toast.error("user not found");
      }

      if (!form.currentPassword) {
        return toast.error("current password required");
      }

      if (!form.newPassword) {
        return toast.error("new password required");
      }

      if (form.newPassword !== form.confirmPassword) {
        return toast.error("passwords do not match");
      }

      setLoading(true);

      await changeUserPassword(form.id, {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });

      toast.success("password updated successfully");

      setForm((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("user"))}
        title={titleCase(t("account"))}
        mainTitle={titleCase(t("account"))}
      />

      <Container fluid>
        <Row>
          {/* ================= PROFILE CARD ================= */}
          <Col md="6">
            <Card>
              <CardHeader>
                <h5>{titleCase(t("account"))}</h5>
              </CardHeader>

              <CardBody>
                <Form onSubmit={handleProfileSubmit}>
                  <FormGroup>
                    <Label>{titleCase(t("name"))}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("username"))}</Label>
                    <Input
                      value={form.username}
                      onChange={(e) =>
                        handleChange("username", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("email"))}</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        handleChange("email", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("role"))}</Label>
                    <Input value={titleCase(form.role)} disabled />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("status"))}</Label>
                    <Input type="select" value={form.status} disabled>
                      <option value="active">
                        {titleCase(t("active"))}
                      </option>
                      <option value="inactive">
                        {titleCase(t("inactive"))}
                      </option>
                    </Input>
                  </FormGroup>

                  <Button color="primary" type="submit" disabled={loading}>
                    {loading
                      ? titleCase(t("saving"))
                      : titleCase(t("save changes"))}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>

          {/* ================= PASSWORD CARD ================= */}
          <Col md="6">
            <Card>
              <CardHeader>
                <h5>{titleCase(t("change password"))}</h5>
              </CardHeader>

              <CardBody>
                <Form onSubmit={handlePasswordSubmit}>
                  <FormGroup>
                    <Label>{titleCase(t("current password"))}</Label>

                    <div style={{ position: "relative" }}>
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        value={form.currentPassword}
                        onChange={(e) => handleChange("currentPassword", e.target.value)}
                        required
                        style={{ paddingRight: "50px" }}
                      />

                      <span
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          fontSize: "12px",
                          userSelect: "none",
                        }}
                        className="text-primary"
                      >
                        {showCurrentPassword ? "hide" : "show"}
                      </span>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("new password"))}</Label>

                    <div style={{ position: "relative" }}>
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={form.newPassword}
                        onChange={(e) => handleChange("newPassword", e.target.value)}
                        required
                        style={{ paddingRight: "50px" }}
                      />

                      <span
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          fontSize: "12px",
                          userSelect: "none",
                        }}
                        className="text-primary"
                      >
                        {showNewPassword ? "hide" : "show"}
                      </span>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("confirm new password"))}</Label>

                    <div style={{ position: "relative" }}>
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        value={form.confirmPassword}
                        onChange={(e) => handleChange("confirmPassword", e.target.value)}
                        required
                        style={{ paddingRight: "50px" }}
                      />

                      <span
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          fontSize: "12px",
                          userSelect: "none",
                        }}
                        className="text-primary"
                      >
                        {showConfirmPassword ? "hide" : "show"}
                      </span>
                    </div>
                  </FormGroup>

                  <Button color="primary" type="submit" disabled={loading}>
                    {loading
                      ? titleCase(t("saving"))
                      : titleCase(t("update password"))}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default Profile;